/**
 * Persistent floating banner that surfaces failed sync items. Shown
 * only when the queue contains rows in status='failed' — invisible
 * otherwise, so reps with a clean queue see nothing. Tapping the
 * banner navigates to /sync-issues for retry/discard.
 *
 * Event-driven: refreshes on `rdm:sync-enqueued` (new op queued or
 * retry kicked) and `rdm:sync-failed` (engine moved a row to terminal
 * failed state). No polling timer — that pollutes test state and isn't
 * needed since every state change has an event.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { STORES, getAll } from '../utils/indexedDB';

export function SyncIssuesBanner() {
  const [failedCount, setFailedCount] = useState(0);

  useEffect(() => {
    let cancelled = false;

    async function check() {
      try {
        const rows = (await getAll(STORES.VISIT_SYNC_QUEUE)) as Array<{ status?: string }>;
        const failed = rows.filter((r) => r.status === 'failed').length;
        if (!cancelled) setFailedCount(failed);
      } catch {
        if (!cancelled) setFailedCount(0);
      }
    }

    void check();
    const onEvent = () => void check();
    window.addEventListener('rdm:sync-failed', onEvent);
    window.addEventListener('rdm:sync-enqueued', onEvent);

    return () => {
      cancelled = true;
      window.removeEventListener('rdm:sync-failed', onEvent);
      window.removeEventListener('rdm:sync-enqueued', onEvent);
    };
  }, []);

  if (failedCount === 0) return null;

  // Slim single-line bar (#2021) — obvious (red) but far less intrusive than the
  // old large block. The chip popup carries the per-type detail + Sync Now.
  return (
    <div className="pointer-events-auto">
      <Link
        to="/sync-issues"
        className="flex items-center justify-between gap-3 rounded-lg bg-red-700/95 px-3 py-1.5 text-white shadow"
      >
        <span className="flex items-center gap-1.5 text-xs font-medium">
          <span aria-hidden>⚠</span>
          {failedCount} {failedCount === 1 ? 'item' : 'items'} failed to sync
        </span>
        <span className="text-xs font-semibold underline">Review</span>
      </Link>
    </div>
  );
}
